/* eslint-disable jsdoc/require-jsdoc, no-nested-ternary, consistent-return */
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';
import { canUseBleAdmin } from '../_helpers/featureAccess';

const BASE = `${DEV_NEW_DOMAIN_URL}/ble-pings/admin`;
const PAGE_LIMIT = 200;

// The measurement payload is schema-agnostic now: each ping carries a `values`
// bag with arbitrary keys/types that vary per ping and per sensor. Keep the type
// loose on purpose — we render whatever arrives rather than a fixed schema.
type PingValue = number | string | boolean | null;

type BlePing = {
  id: number;
  clientPingId: string;
  username: string | null;
  clientId: number | null;
  deviceSerial: number | null;
  deviceName: string | null;
  rssi: number | null;
  seq: number | null;
  observedAt: number | null;
  reportedAt: number | null;
  appState: string | null;
  createdAt: number | null;
  values: Record<string, PingValue> | null;
  // Phone-side context at observation time — where the phone was (GPS) and
  // how it was moving (accelerometer, in g). Null when location was denied or
  // stale, and on rows from app builds that predate these fields.
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  locationAccuracy: number | null;
  locationAt: number | null;
  accelX: number | null;
  accelY: number | null;
  accelZ: number | null;
};

type BleStats = {
  totalPings: number;
  distinctDevices: number;
  distinctUsers: number;
  backgroundPings: number;
};

function formatTime(value: number | null): string {
  if (value === null || value === undefined) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

// Turn an arbitrary measurement key into a readable header. Underscores become
// spaces; the raw key is a fine fallback since we can't know the schema.
function prettyKey(key: string): string {
  return key.replace(/_/g, ' ');
}

// Render an arbitrary `values` entry safely. Numbers show as-is with long
// decimals trimmed, booleans as Yes/No, strings as-is, and anything empty or
// unrenderable ("null"/"NaN"/"[object Object]") collapses to an em-dash.
function formatValue(value: PingValue | undefined): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    if (Number.isInteger(value)) return String(value);
    // Trim to at most 3 decimals, then drop any trailing zeros (23.400 -> 23.4).
    return String(Number(value.toFixed(3)));
  }
  if (typeof value === 'string') return value;
  // Should not happen given the type, but never leak "[object Object]".
  try {
    return JSON.stringify(value);
  } catch {
    return '—';
  }
}

function appStateBadge(appState: string | null): string {
  const s = (appState || '').toLowerCase();
  // "background" is the interesting case — proving background collection works is
  // the entire point of this feature, so it gets the loud treatment.
  if (s === 'background')
    return 'bg-success-500/20 text-success-400 border-success-500/50 font-semibold';
  if (s === 'foreground') return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
  return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
}

// RSSI runs roughly -30 (strong) to -100 (weak). Colour the number and show a
// 4-bar strength meter next to it.
function rssiBars(rssi: number | null): number {
  if (rssi === null || rssi === undefined) return 0;
  if (rssi >= -55) return 4;
  if (rssi >= -70) return 3;
  if (rssi >= -85) return 2;
  return 1;
}

function rssiColor(rssi: number | null): string {
  const bars = rssiBars(rssi);
  if (bars >= 4) return 'text-success-400';
  if (bars === 3) return 'text-success-400';
  if (bars === 2) return 'text-warning-300';
  if (bars === 1) return 'text-error-400';
  return 'text-gray-500';
}

function RssiCell({ rssi }: { rssi: number | null }): ReactElement {
  if (rssi === null || rssi === undefined) {
    return <span className="text-gray-500">—</span>;
  }
  const bars = rssiBars(rssi);
  return (
    <span className={`inline-flex items-center gap-1.5 ${rssiColor(rssi)}`}>
      <span className="inline-flex items-end gap-[2px]" aria-hidden="true">
        {[1, 2, 3, 4].map((b) => (
          <span
            key={b}
            className={`w-[3px] rounded-sm ${b <= bars ? 'bg-current' : 'bg-gray-700'}`}
            style={{ height: `${b * 3 + 2}px` }}
          />
        ))}
      </span>
      <span className="font-mono text-xs">{rssi}</span>
    </span>
  );
}

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

// "12s" / "4m" — how much older the GPS fix is than the ping itself.
function formatAge(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.round(s / 60)}m`;
}

function LocationCell({ ping }: { ping: BlePing }): ReactElement {
  const { latitude: lat, longitude: lng } = ping;
  if (!isNum(lat) || !isNum(lng)) {
    return <span className="text-gray-500">—</span>;
  }
  // Only call out the fix's age when it's noticeably older than the ping —
  // a few seconds is normal GPS cadence, minutes means updates had stopped.
  const age =
    isNum(ping.locationAt) && isNum(ping.observedAt) ? ping.observedAt - ping.locationAt : 0;
  const details = [
    isNum(ping.altitude) ? `alt ${Math.round(ping.altitude)} m` : null,
    isNum(ping.locationAccuracy) ? `±${Math.round(ping.locationAccuracy)} m` : null,
    age > 30000 ? `fix ${formatAge(age)} old` : null
  ].filter(Boolean);
  return (
    <div className="whitespace-nowrap">
      <a
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-xs text-primary-400 hover:underline"
      >
        <span className="material-icons-outlined text-sm">place</span>
        {lat.toFixed(5)}, {lng.toFixed(5)}
      </a>
      {details.length > 0 && (
        <div className="text-xs text-gray-500">{details.join(' · ')}</div>
      )}
    </div>
  );
}

function AccelCell({ ping }: { ping: BlePing }): ReactElement {
  const { accelX: x, accelY: y, accelZ: z } = ping;
  if (!isNum(x) || !isNum(y) || !isNum(z)) {
    return <span className="text-gray-500">—</span>;
  }
  // Magnitude ≈ 1 g means the phone was at rest (gravity only); the further
  // from 1 it is, the more the phone was moving.
  const magnitude = Math.sqrt(x * x + y * y + z * z);
  return (
    <div className="whitespace-nowrap font-mono text-xs">
      <div className="text-gray-200">
        {x.toFixed(2)}, {y.toFixed(2)}, {z.toFixed(2)}
      </div>
      <div className="text-gray-500">|a| {magnitude.toFixed(2)} g</div>
    </div>
  );
}

/**
 * Admin-only page: the raw BLE live-ping firehose. Access is gated both
 * client-side (redirect) and server-side (the /ble-pings/admin/* API returns 403
 * to anyone but the admin), so this is a convenience view, not the security
 * boundary.
 * @returns The admin BLE-pings page.
 */
export default function AdminBlePingsPage(): ReactElement {
  const userEmail = useMemo(() => getUserNameFromSessionStorage(), []);
  const isAllowed = useMemo(() => canUseBleAdmin(userEmail), [userEmail]);

  const [pings, setPings] = useState<BlePing[]>([]);
  const [stats, setStats] = useState<BleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [serialInput, setSerialInput] = useState('');
  const [serial, setSerial] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  // Off by default on purpose — no background polling unless the admin asks.
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const params: Record<string, string | number> = { limit: PAGE_LIMIT, offset: 0 };
        if (serial.trim()) params.serial = serial.trim();
        const [pingsRes, statsRes] = await Promise.all([
          axiosInstance.get<BlePing[]>(`${BASE}/pings`, { params }),
          axiosInstance.get<BleStats>(`${BASE}/stats`)
        ]);
        if (cancelled) return;
        setPings(Array.isArray(pingsRes.data) ? pingsRes.data : []);
        setStats(statsRes.data || null);
        setError(null);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load BLE pings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAllowed, serial, reloadKey]);

  useEffect(() => {
    if (!isAllowed || !autoRefresh) return;
    const timer = setInterval(() => setReloadKey((k) => k + 1), 15000);
    return () => clearInterval(timer);
  }, [isAllowed, autoRefresh]);

  const refresh = useCallback(() => {
    setLoading(true);
    setReloadKey((k) => k + 1);
  }, []);

  // The measurement columns are dynamic: take the union of every `values` key
  // seen across the currently-loaded pings, sorted for stable ordering. Each row
  // then fills its cells from that ping's own `values` (missing key -> em-dash).
  const valueKeys = useMemo(() => {
    const keys = new Set<string>();
    pings.forEach((p) => {
      if (p.values && typeof p.values === 'object') {
        Object.keys(p.values).forEach((k) => keys.add(k));
      }
    });
    return Array.from(keys).sort();
  }, [pings]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const backgroundShare =
    stats && stats.totalPings > 0
      ? Math.round((stats.backgroundPings / stats.totalPings) * 100)
      : 0;

  // Fixed envelope columns + one column per dynamic measurement key.
  const columnCount = 8 + valueKeys.length;

  return (
    <div className="flex flex-1 flex-col gap-6 px-6 py-6 text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">BLE Live Pings (Admin)</h1>
          <p className="text-sm text-gray-400">Signed in as {userEmail}</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Total Pings</div>
            <div className="text-xl font-semibold">{stats?.totalPings ?? '—'}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Devices</div>
            <div className="text-xl font-semibold">{stats?.distinctDevices ?? '—'}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Users</div>
            <div className="text-xl font-semibold">{stats?.distinctUsers ?? '—'}</div>
          </div>
          <div className="rounded border border-success-500/40 bg-container-500 px-4 py-3 text-sm">
            <div className="text-success-400">Background Pings</div>
            <div className="text-xl font-semibold text-success-400">
              {stats?.backgroundPings ?? '—'}
              {stats && stats.totalPings > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-400">
                  {backgroundShare}%
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={serialInput}
          onChange={(e) => setSerialInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSerial(serialInput);
          }}
          placeholder="Filter by serial…"
          className="w-52 rounded border border-gray-800 bg-container-500 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-primary-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setSerial(serialInput)}
          className="rounded border border-gray-800 bg-container-500 px-3 py-2 text-sm text-gray-200 transition hover:bg-background-500"
        >
          Apply
        </button>
        {serial && (
          <button
            type="button"
            onClick={() => {
              setSerialInput('');
              setSerial('');
            }}
            className="rounded border border-gray-800 bg-container-500 px-3 py-2 text-sm text-gray-400 transition hover:bg-background-500"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-1.5 rounded border border-primary-500/40 bg-primary-500/15 px-3 py-2 text-sm text-primary-400 transition hover:bg-primary-500/25"
        >
          <span className="material-icons-outlined text-base">refresh</span>
          Refresh
        </button>
        <Link
          to="/admin/ble-live"
          className="flex items-center gap-1.5 rounded border border-success-500/40 bg-success-500/15 px-3 py-2 text-sm text-success-400 transition hover:bg-success-500/25"
        >
          <span className="material-icons-outlined text-base">show_chart</span>
          Live graphs
        </Link>
        <label
          htmlFor="ble-pings-auto-refresh"
          className="flex cursor-pointer select-none items-center gap-2 text-sm text-gray-400"
        >
          <input
            id="ble-pings-auto-refresh"
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="accent-primary-500"
          />
          Auto-refresh (15s)
        </label>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-400">
          <span className="material-icons-outlined animate-spin">autorenew</span>
          Loading…
        </div>
      ) : error ? (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {error}
        </div>
      ) : (
        <section className="rounded-lg border border-gray-800 bg-container-500">
          <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
            <h2 className="text-lg font-semibold">Recent Pings</h2>
            <span className="text-sm text-gray-400">
              {pings.length} records{serial ? ` · serial ${serial}` : ''}
            </span>
          </div>
          {/* The measurement key set is dynamic and can be wide, so the table
              scrolls horizontally on its own — the page body never does. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background-500 text-left text-gray-400">
                <tr>
                  <th className="px-4 py-2">Observed</th>
                  <th className="px-4 py-2">Device</th>
                  <th className="px-4 py-2">App State</th>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">RSSI</th>
                  <th className="px-4 py-2">Seq</th>
                  <th className="px-4 py-2">Phone Location</th>
                  <th className="whitespace-nowrap px-4 py-2">Accel (x, y, z g)</th>
                  {valueKeys.map((k) => (
                    <th key={k} className="whitespace-nowrap px-4 py-2 capitalize">
                      {prettyKey(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pings.map((p) => (
                  <tr key={p.clientPingId || p.id} className="border-t border-gray-800">
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-300">
                      {formatTime(p.observedAt)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-gray-100">{p.deviceName || '—'}</div>
                      <div className="font-mono text-xs text-gray-500">
                        {p.deviceSerial ?? '—'}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block rounded border px-2 py-0.5 text-xs ${appStateBadge(
                          p.appState
                        )}`}
                      >
                        {p.appState || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">{p.username || '—'}</td>
                    <td className="px-4 py-2">
                      <RssiCell rssi={p.rssi} />
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-300">
                      {p.seq ?? '—'}
                    </td>
                    <td className="px-4 py-2">
                      <LocationCell ping={p} />
                    </td>
                    <td className="px-4 py-2">
                      <AccelCell ping={p} />
                    </td>
                    {valueKeys.map((k) => (
                      <td
                        key={k}
                        className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-200"
                      >
                        {formatValue(p.values ? p.values[k] : undefined)}
                      </td>
                    ))}
                  </tr>
                ))}
                {pings.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={columnCount}>
                      No pings
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
